/** One judge answer per Human Test, as stored on `walkthroughs.verdict`. */
export type Verdict = {
  humanTestId: string;
  question: string;
  rating: "yes" | "partially" | "no" | "not_applicable";
  reasoning: string;
};

/**
 * Match a binding's latest verdict entry to a research question. Human-test
 * ids are only unique within one outline, and questions can be re-worded or
 * re-id'd after a run (edits regenerate ids), so fall back to the question
 * text, then to position when the verdict still has one entry per question.
 */
export function findVerdictForQuestion(
  verdicts: Verdict[] | null | undefined,
  questions: Array<{ id: string; question: string }>,
  index: number,
): Verdict | null {
  // `walkthroughs.verdict` is jsonb — tolerate legacy non-array payloads.
  if (!Array.isArray(verdicts) || verdicts.length === 0) return null;
  const question = questions[index];
  if (!question) return null;
  const byId = verdicts.find((v) => v.humanTestId === question.id);
  if (byId) return byId;
  const text = normalizeQuestion(question.question);
  const byText = verdicts.find((v) => normalizeQuestion(v.question) === text);
  if (byText) return byText;
  // Positional fallback only when neither ids nor texts line up for ANY
  // question — otherwise a partially-matching verdict would be misattributed.
  const anyMatches = questions.some(
    (q) =>
      verdicts.some((v) => v.humanTestId === q.id) ||
      verdicts.some(
        (v) => normalizeQuestion(v.question) === normalizeQuestion(q.question),
      ),
  );
  if (anyMatches || verdicts.length !== questions.length) return null;
  return verdicts[index] ?? null;
}

/**
 * The ratings one research question received across runs, in the order the
 * runs are given. Stricter than `findVerdictForQuestion`: a run counts only
 * when its verdict answered the *same* question. Human-test ids are reused
 * ("ht-1") when a question is rewritten, so a verdict that carries question
 * text must match the current text; the id decides only when it carries none.
 * Runs that did not answer this question are skipped.
 */
export function questionRatingHistory(
  runVerdicts: ReadonlyArray<unknown>,
  question: { id: string; question: string },
): Verdict["rating"][] {
  const text = normalizeQuestion(question.question);
  const ratings: Verdict["rating"][] = [];
  for (const verdicts of runVerdicts) {
    if (!Array.isArray(verdicts)) continue;
    const match = (verdicts as Partial<Verdict>[]).find((v) =>
      v.question
        ? normalizeQuestion(v.question) === text
        : v.humanTestId === question.id,
    );
    if (match?.rating) ratings.push(match.rating);
  }
  return ratings;
}

/**
 * "No → No → Partially across 3 runs", or "Yes in all 2 runs" when the
 * rating never moved. Null for fewer than two answers — nothing to compare.
 */
export function describeRatingHistory(
  ratings: readonly Verdict["rating"][],
  label: (rating: Verdict["rating"]) => string,
): string | null {
  if (ratings.length < 2) return null;
  const n = ratings.length;
  if (ratings.every((r) => r === ratings[0])) {
    return `${label(ratings[0])} in all ${n} runs`;
  }
  return `${ratings.map(label).join(" → ")} across ${n} runs`;
}

function normalizeQuestion(text: string | null | undefined): string {
  return (text ?? "").trim().replace(/\s+/g, " ").toLowerCase();
}
